import Fuse from "fuse.js";
import { createSelector } from "reselect";
import { EMPTY_ARRAY } from "lib";
import {
  SideLoadedBusinessRow,
  SideLoadedValidation,
} from "constants/sideLoadedBusinesses";
import {
  FUSE_OPTIONS,
  formatSearchNameField,
  formatSearchQueryTokens,
  matchSideLoadedBusinessIdentifiers,
  SearchFormattedSideLoadedBusiness,
  SideLoadedBusinessWithMatches,
} from "lib/sideLoadedBusinesses/search";
import { createSideLoadedSuggestionMatcher } from "lib/sideLoadedBusinesses/suggestionMatcher";
import {
  getSideLoadedBusinessValidationIssues,
  isSideLoadedBusinessValid,
} from "lib/sideLoadedBusinesses/validation";
import { getSideLoadedBusinesses, getSideLoadedDataSets } from "./root";
import { selectSideLoadedDataSetLabelsById } from "./sideLoadedDataSets";
import { selectSideLoadedDataMappingEntriesByDataSetIdLookup } from "./sideLoadedDataMappings";
import {
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "./indexes";

export { matchSideLoadedBusinessIdentifiers };
export type {
  SearchFormattedSideLoadedBusiness,
  SideLoadedBusinessWithMatches,
};

export const getSideLoadedBusinessById =
  (recordId: string) => (state: unknown) =>
    getSideLoadedBusinesses(state)[recordId] || null;

export const selectSideLoadedBusinessesList = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses),
);

export const selectSideLoadedBusinessesByDatasetId =
  (datasetId: string) =>
  (state: unknown): SideLoadedBusiness[] =>
    selectSideLoadedBusinessesList(state).filter(
      business => business.datasetId === datasetId,
    );

export interface SideLoadedValidationContext {
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
  mappingEntriesByDataSetId: Record<string, SideLoadedDataMappingEntry[]>;
}

export const selectSideLoadedValidationContext = createSelector(
  [
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
    selectSideLoadedDataMappingEntriesByDataSetIdLookup,
  ],
  (
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  ): SideLoadedValidationContext => ({
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  }),
);

interface CachedValidation {
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
  entries: SideLoadedDataMappingEntry[] | undefined;
  validation: SideLoadedValidation;
}

const validationCache = new WeakMap<SideLoadedBusiness, CachedValidation>();

const getCachedValidation = (
  record: SideLoadedBusiness,
  {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  }: SideLoadedValidationContext,
): SideLoadedValidation => {
  const entries = mappingEntriesByDataSetId[record.datasetId];
  const cached = validationCache.get(record);
  if (
    cached &&
    cached.businessIdByAgentCodeLookup === businessIdByAgentCodeLookup &&
    cached.businessIdBySaleyardCodeLookup === businessIdBySaleyardCodeLookup &&
    cached.entries === entries
  ) {
    return cached.validation;
  }
  const options = {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    entries,
  };
  const isValid = isSideLoadedBusinessValid(record, options);
  let issues: string[] | null = isValid ? [] : null;
  const validation: SideLoadedValidation = {
    isValid,
    getValidationIssues: () => {
      issues ??= getSideLoadedBusinessValidationIssues(record, options);
      return issues;
    },
  };
  validationCache.set(record, {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    entries,
    validation,
  });
  return validation;
};

// Carried on the row rather than resolved in a valueGetter: the grid groups on
// this value, and a change to it has to reach the grid as a row data change.
export const selectSideLoadedBusinessesGridList = createSelector(
  [
    selectSideLoadedBusinessesList,
    selectSideLoadedDataSetLabelsById,
    selectSideLoadedValidationContext,
  ],
  (
    sideLoadedBusinesses: SideLoadedBusiness[],
    labelsById: Record<string, string>,
    validationContext: SideLoadedValidationContext,
  ): SideLoadedBusinessRow[] =>
    sideLoadedBusinesses.map(sideLoadedBusiness => ({
      id: sideLoadedBusiness.id,
      sideLoadedBusiness,
      // Falls back to the name until the data sets have loaded.
      datasetLabel:
        labelsById[sideLoadedBusiness.datasetId] ||
        sideLoadedBusiness.datasetName,
      // Validated when the grid reads the row, not up front.
      getValidation: () =>
        getCachedValidation(sideLoadedBusiness, validationContext),
    })),
);

export const getSideLoadedBusinessesCount = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses).length,
);

export const selectSideLoadedBusinessSearchIndex = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) => {
    const searchFormattedBusinesses: SearchFormattedSideLoadedBusiness[] =
      Object.values(sideLoadedBusinesses).map(sideLoadedBusiness => ({
        sideLoadedBusiness,
        formattedName: formatSearchNameField(sideLoadedBusiness.name),
      }));
    return new Fuse(searchFormattedBusinesses, FUSE_OPTIONS);
  },
);

export const searchSideLoadedBusinesses =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): Fuse.FuseResult<SearchFormattedSideLoadedBusiness>[] => {
    const formattedQueryTokens = formatSearchQueryTokens(query);
    if (!formattedQueryTokens.length) {
      return EMPTY_ARRAY;
    }

    const index = selectSideLoadedBusinessSearchIndex(state);

    const results = strict
      ? // strict search - get matches for all supplied tokens (AND = " ")
        index.search(formattedQueryTokens.join(" "))
      : // non-strict search - match any supplied tokens (OR = "|")
        index.search(formattedQueryTokens.join("|"));

    // Filtered on the results rather than the index so the one index still
    // serves the suggestion count and the grid's show-ignored view.
    if (includeIgnored) {
      return results;
    }
    return results.filter(result => !result.item.sideLoadedBusiness.isIgnored);
  };

export const searchSideLoadedBusinessesWithMatches =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): SideLoadedBusinessWithMatches[] => {
    const businesses = getSideLoadedBusinesses(state);
    const identifierMatches = matchSideLoadedBusinessIdentifiers(
      Object.values(businesses),
      query,
    ).filter(match => includeIgnored || !match.sideLoadedBusiness.isIgnored);
    const identifierMatchIds = new Set(
      identifierMatches.map(match => match.sideLoadedBusiness.id),
    );

    const fuzzyMatches = searchSideLoadedBusinesses(
      query,
      strict,
      includeIgnored,
    )(state)
      .filter(
        result => !identifierMatchIds.has(result.item.sideLoadedBusiness.id),
      )
      .map(result => ({
        sideLoadedBusiness: result.item.sideLoadedBusiness,
        matchedFields: EMPTY_ARRAY,
      }));

    return identifierMatches.concat(fuzzyMatches);
  };

export const isHiddenSideLoadedBusiness = (
  business: Pick<SideLoadedBusiness, "isIgnored" | "businessIds">,
): boolean => business.isIgnored || business.businessIds.length > 0;

// The strict/liberal choice must be made on what's actually visible -
// otherwise a strict match that's only a currently-hidden record starves the
// liberal fallback of a less-exact, visible match it might have surfaced.
export function pickSideLoadedSearchResults(
  strictResults: SideLoadedBusinessWithMatches[],
  liberalResults: SideLoadedBusinessWithMatches[],
  showHidden: boolean,
): SideLoadedBusinessWithMatches[] {
  const visibleStrictResults = strictResults.filter(
    result =>
      showHidden || !isHiddenSideLoadedBusiness(result.sideLoadedBusiness),
  );
  return visibleStrictResults.length ? strictResults : liberalResults;
}

// Counts new suggestions only - used records are left in the search results
// for the grid, which labels them, but they are not work waiting to be done.
export const newSideLoadedBusinessSuggestionsCount =
  (query: string) =>
  (state: State): number =>
    searchSideLoadedBusinessesWithMatches(
      query,
      true,
    )(state).filter(result => !result.sideLoadedBusiness.businessIds.length)
      .length;

const NAME_AND_ABN_FIRST = ["name", "abn"];

// Not a comparator: this ordering isn't antisymmetric, so a sort would depend on
// the engine's internals.
const orderMappableFields = (columns: string[]): string[] => [
  ...NAME_AND_ABN_FIRST.filter(column => columns.includes(column)),
  ...columns.filter(column => !NAME_AND_ABN_FIRST.includes(column)),
];

export const selectSideLoadedBusinessMappableFieldsByIdLookup = createSelector(
  [getSideLoadedBusinesses, getSideLoadedDataSets],
  (
    sideLoadedBusinesses: Record<string, SideLoadedBusiness>,
    sideLoadedDataSets: Record<string, SideLoadedDataSet>,
  ) => {
    // Per data set, not per record: every record of one file has the same
    // columns, but two files do not.
    const columnsByDataSetId: Record<string, string[]> = {};

    Object.values(sideLoadedBusinesses).forEach(sideLoadedBusiness => {
      if (columnsByDataSetId[sideLoadedBusiness.datasetId]) {
        return;
      }
      // The data set's own columns are the file's true order; a record's keys
      // are the fallback until the data set has loaded.
      const sourceColumns =
        sideLoadedDataSets[sideLoadedBusiness.datasetId]?.sourceColumns;
      columnsByDataSetId[sideLoadedBusiness.datasetId] = orderMappableFields(
        sourceColumns?.length
          ? sourceColumns
          : Object.keys(sideLoadedBusiness.rawData),
      );
    });

    return Object.values(sideLoadedBusinesses).reduce<Record<string, string[]>>(
      (acc, sideLoadedBusiness) => {
        acc[sideLoadedBusiness.id] =
          columnsByDataSetId[sideLoadedBusiness.datasetId];
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableFieldsById =
  (recordId: string) => (state: State) =>
    selectSideLoadedBusinessMappableFieldsByIdLookup(state)[recordId] ||
    EMPTY_ARRAY;

const selectSideLoadedBusinessMappableOptionsByIdLookup = createSelector(
  [getSideLoadedBusinesses, selectSideLoadedBusinessMappableFieldsByIdLookup],
  (sideLoadedBusinesses, mappableFieldsByIdLookup) => {
    return Object.values(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusiness: SideLoadedBusiness) => {
        const mappableFields = mappableFieldsByIdLookup[sideLoadedBusiness.id];
        // Show only mappable fields with data.
        acc[sideLoadedBusiness.id] = mappableFields
          .filter(field => Boolean(sideLoadedBusiness?.rawData[field]))
          .reduce((rowAcc, field) => {
            const fieldValue = sideLoadedBusiness?.rawData[field];

            rowAcc.push({
              label: fieldValue,
              value: field,
              disabled: !fieldValue,
            });

            return rowAcc;
          }, []);
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableOptionsById =
  (recordId: string) => (state: State) => {
    return (
      selectSideLoadedBusinessMappableOptionsByIdLookup(state)[recordId] ||
      EMPTY_ARRAY
    );
  };

const matchKeyByRecord = new WeakMap<SideLoadedBusiness, string>();

const getMatchKey = (record: SideLoadedBusiness): string => {
  let matchKey = matchKeyByRecord.get(record);
  if (matchKey === undefined) {
    matchKey = [
      record.id,
      record.name,
      record.abn,
      record.brands,
      record.email,
      record.pics,
    ].join("\u0000");
    matchKeyByRecord.set(record, matchKey);
  }
  return matchKey;
};

const haveSameMatchKeys = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((matchKey, i) => matchKey === b[i]);

// Survives records being ignored or linked to a business - which replaces the
// record but changes nothing it is matched on - so the matcher's caches do too.
const selectSideLoadedSuggestionMatchSource = createSelector(
  [selectSideLoadedBusinessesList],
  (records: SideLoadedBusiness[]) => ({
    matchKeys: records.map(getMatchKey),
    records,
  }),
  {
    memoizeOptions: {
      resultEqualityCheck: (a, b) =>
        haveSameMatchKeys(a.matchKeys, b.matchKeys),
    },
  },
);

export const selectSideLoadedSuggestionMatcher = createSelector(
  [selectSideLoadedSuggestionMatchSource],
  ({ records }) => createSideLoadedSuggestionMatcher(records),
);
